import React, { useState, useEffect, useCallback } from "react"
import { CardBody, Table } from "reactstrap"
import { Calendar, momentLocalizer } from "react-big-calendar"
import moment from "moment-timezone"
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "react-big-calendar/lib/addons/dragAndDrop/styles.css"
import { MdTimer, MdContentCopy } from "react-icons/md"
import CustomCalendarToolbar from "components/Calendar/CustomCalendarToolbar"
import WorkoutModal from "pages/Workouts/addWorkout"
import Swal from "sweetalert2"
import { FetchPutData } from "utils/Common_api_functions"

moment.updateLocale("en-gb", {
  week: { dow: 1 },
})
moment.tz.setDefault("UTC")

const DnDCalendar = withDragAndDrop(Calendar)
const localizer = momentLocalizer(moment)

const UserCalendar = ({
  events,
  draggedWorkoutRef,
  selectedUser,
  setSelectedUser,
  userOptions,
  handleSelectEvent,
  activities,
  handleDropFromOutside,
  onEventResize,
  onEventDrop,
  getDraggedEvent,
  handleDeleteEvent,
  setEvents,
  setallUsers,
  setUsers,
  setLoading,
}) => {
  const [userworkoutmodal, seruserworkoutmodal] = useState(false)
  const [startDate, setStartDate] = useState("")
  const [copiedEvent, setCopiedEvent] = useState(null) // ← clipboard state

  // Clear clipboard on Escape
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "Escape") setCopiedEvent(null)
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  const handleCreateWorkoutforuser = useCallback(
    async (workoutData, targetDate) => {
      const resolvedDate = targetDate || startDate
      const newEvent = {
        title: workoutData.activityTitle || "Untitled Workout",
        start: resolvedDate,
        end: resolvedDate,
        allDay: true,
        workout: workoutData,
      }

      setEvents(prev => [...prev, newEvent])

      try {
        const APIdata = await FetchPutData(
          setLoading,
          "user/insert/workout",
          selectedUser,
          { event: newEvent }
        )

        if (!APIdata?.data?.success) throw new Error(APIdata?.res?.message)

        const updatedEvents =
          APIdata?.data?.events || APIdata?.data?.workoutCalender || []
        const normalized = updatedEvents.map(ev => ({
          ...ev,
          title: ev.title || ev.workout?.activityTitle || "Untitled Workout",
          start: new Date(ev.start),
          end: new Date(ev.end),
        }))
        setallUsers(prev =>
          prev.map(u =>
            u._id === selectedUser ? { ...u, workoutCalender: normalized } : u
          )
        )
        setUsers(prev =>
          prev.map(c => ({
            ...c,
            users: c.users.map(u =>
              u._id === selectedUser ? { ...u, workoutCalender: normalized } : u
            ),
          }))
        )
        setEvents(normalized)
      } catch (err) {
        Swal.fire({
          title: "Error",
          icon: "error",
          text: err.message || "Failed to add workout",
        })
        setEvents(prev => prev.filter(ev => ev !== newEvent))
      }

      draggedWorkoutRef.current = null
    },
    [
      startDate,
      selectedUser,
      setEvents,
      setallUsers,
      setUsers,
      setLoading,
      draggedWorkoutRef,
    ]
  )

  // Paste the copied event onto a clicked slot
  const handleSlotSelect = useCallback(
    slotInfo => {
      if (copiedEvent) {
        // Paste mode: skip the modal, directly insert the copied workout
        handleCreateWorkoutforuser(copiedEvent.workout, slotInfo.start)
        // Keep clipboard so the user can paste again; clear if you prefer one-shot:
        // setCopiedEvent(null)
      } else {
        setStartDate(slotInfo.start)
        seruserworkoutmodal(true)
      }
    },
    [copiedEvent, handleCreateWorkoutforuser]
  )

  const CustomEvent = event => {
    const event1 = event?.event

    const handleCopy = e => {
      e.stopPropagation()
      setCopiedEvent(event1)
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "2px 10px 2px 2px",
          margin: "0px 2px",
          position: "relative",
          outline:
            copiedEvent && copiedEvent === event1
              ? "2px solid #4ade80"
              : "none",
          borderRadius: 4,
        }}
        key={event?._id}
      >
        <div style={{ maxWidth: "90%", overflow: "hidden" }}>
          <div className="fw-bold mb-1" style={{ fontSize: "12px" }}>
            {
              activities?.find(
                item => item?.name === event?.event?.workout?.activityName
              )?.icon
            }{" "}
            {event?.title || "Untitled Event"}
          </div>
          <div style={{ fontSize: "12px" }} className="mb-2">
            <MdTimer className="me-1" size={14} />
            {event1?.workout?.plannedDuration}
          </div>
          <Table className="mb-0 text-white gap-2">
            {event1?.workout?.buildWorkout?.map((item, index) => (
              <tr key={index}>
                <td style={{ fontSize: "12px" }} className="pe-2">
                  {item?.typeOption
                    ? item.typeOption.charAt(0).toUpperCase()
                    : ""}
                </td>
                <td style={{ fontSize: "12px" }} className="pe-2">
                  {item?.duration}
                </td>
                <td style={{ fontSize: "12px" }}>
                  {item?.zone ? item.zone.replace(/zone/gi, "Z") : ""}
                </td>
              </tr>
            ))}
          </Table>
        </div>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          title="Copy workout"
          style={{
            backgroundColor: "rgba(255,255,255)",
            border: "none",
            borderRadius: "2px",
            color: "#3b82f6",
            cursor: "pointer",
            position: "absolute",
            right: "25px",
            top: "20%",
          }}
        >
          <MdContentCopy size={14} />
        </button>

        {/* Delete button */}
        <button
          onClick={e => {
            e.stopPropagation()
            handleDeleteEvent(event)
          }}
          style={{
            backgroundColor: "rgba(255,255,255)",
            border: "none",
            borderRadius: "2px",
            color: "red",
            cursor: "pointer",
            position: "absolute",
            right: "2px",
            top: "20%",
          }}
        >
          ✕
        </button>
      </div>
    )
  }

  return (
    <CardBody>
      {/* Paste-mode banner */}
      {copiedEvent && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#1e40af",
            color: "#fff",
            borderRadius: 6,
            padding: "6px 14px",
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          <span>
            📋 <strong>{copiedEvent.title || "Untitled Workout"}</strong> copied
            — click any date to paste. Press{" "}
            <kbd
              style={{
                background: "rgba(255,255,255,0.2)",
                borderRadius: 3,
                padding: "0 4px",
              }}
            >
              Esc
            </kbd>{" "}
            to cancel.
          </span>
          <button
            onClick={() => setCopiedEvent(null)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      )}

      <DnDCalendar
        localizer={localizer}
        events={events}
        defaultView="month"
        onEventDrop={onEventDrop}
        onEventResize={onEventResize}
        resizable
        selectable={true}
        popup
        dragFromOutsideItem={getDraggedEvent}
        onDropFromOutside={handleDropFromOutside}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSlotSelect} // ← uses paste-aware handler
        style={{ height: "100%" }}
        components={{
          toolbar: props => (
            <CustomCalendarToolbar
              {...props}
              users={userOptions || []}
              viewingPlanMode={false}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
            />
          ),
          event: CustomEvent,
        }}
      />

      <WorkoutModal
        isOpen={userworkoutmodal}
        toggle={() => seruserworkoutmodal(false)}
        onSave={workoutData => handleCreateWorkoutforuser(workoutData)}
        startDate={startDate}
      />
    </CardBody>
  )
}

export default UserCalendar
